import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TourCard } from "./tour-card";

/*
  ЛЕНТА ТУРОВ.
  Листается пальцем на телефоне, трекпадом и стрелками по бокам на компе,
  а пока её не трогают — медленно едет сама по кругу.

  ВАЖНО: карточки дублируются ТОЛЬКО если туров реально больше, чем влезает
  на экран. Один тур останется одной карточкой — раньше он размножался и
  выглядел так, будто туров много.

  Что можно крутить:
    AUTO_SPEED   — скорость автопрокрутки в пикселях в секунду (меньше = медленнее);
    RESUME_DELAY — через сколько миллисекунд лента поедет сама после того,
                   как человек сам её пролистал.
*/
const AUTO_SPEED = 26;
const RESUME_DELAY = 4000;

const TourRail = ({ tours }) => {
  const railRef = useRef(null);
  const pausedUntil = useRef(0);
  const hovering = useRef(false);
  // true — туры не влезают на экран, значит есть что крутить по кругу
  const [looping, setLooping] = useState(false);

  // Когда крутим по кругу, список идёт ровно два раза: доехав до середины,
  // лента прыгает в начало — стык не виден.
  const list = looping ? [...tours, ...tours] : tours;

  // Решаем, нужно ли зацикливание: сравниваем ширину одного «круга»
  // с шириной экрана. Пересчитываем при смене размера окна.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    const measure = () => {
      const kids = rail.children;
      if (!kids.length) return;
      const roundWidth =
        kids.length > tours.length
          ? kids[tours.length].offsetLeft - kids[0].offsetLeft
          : rail.scrollWidth;
      setLooping(roundWidth > rail.clientWidth + 8);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(rail);
    return () => ro.disconnect();
  }, [tours, looping]);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !looping) return;

    // стартуем с 1px, чтобы свайп вправо в самом начале тоже зациклился
    rail.scrollLeft = 1;

    const pause = () => {
      pausedUntil.current = performance.now() + RESUME_DELAY;
    };
    const onEnter = () => (hovering.current = true);
    const onLeave = () => (hovering.current = false);

    rail.addEventListener("pointerdown", pause);
    rail.addEventListener("touchstart", pause, { passive: true });
    rail.addEventListener("wheel", pause, { passive: true });
    rail.addEventListener("mouseenter", onEnter);
    rail.addEventListener("mouseleave", onLeave);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1); // после вкладки в фоне не прыгаем
      last = now;

      if (!reduced && !hovering.current && now > pausedUntil.current) {
        rail.scrollLeft += AUTO_SPEED * dt;
      }

      // зацикливание в обе стороны
      const half = rail.scrollWidth / 2;
      if (half > 0) {
        if (rail.scrollLeft >= half) rail.scrollLeft -= half;
        else if (rail.scrollLeft <= 0) rail.scrollLeft = half - 1;
      }

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      rail.removeEventListener("pointerdown", pause);
      rail.removeEventListener("touchstart", pause);
      rail.removeEventListener("wheel", pause);
      rail.removeEventListener("mouseenter", onEnter);
      rail.removeEventListener("mouseleave", onLeave);
    };
  }, [tours, looping]);

  // Стрелки на компе: сдвигают ленту ровно на одну карточку.
  const nudge = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    pausedUntil.current = performance.now() + RESUME_DELAY;
    const [a, b] = rail.children;
    const stepPx = b ? b.offsetLeft - a.offsetLeft : rail.clientWidth * 0.8;
    rail.scrollBy({ left: dir * stepPx, behavior: "smooth" });
  };

  return (
    <div className="relative mt-6">
      {/* -mx-4 px-4: на телефоне карточки уходят под самый край экрана */}
      <div ref={railRef} className="tour-rail no-scrollbar -mx-4 px-4">
        {list.map((activity, i) => (
          <div key={`${activity?._id || "t"}-${i}`} className="tour-rail-item">
            <TourCard activity={activity} index={i % tours.length} />
          </div>
        ))}
      </div>

      {/* стрелки нужны только когда есть что листать */}
      {looping && (
        <>
          <RailArrow side="left" onClick={() => nudge(-1)} />
          <RailArrow side="right" onClick={() => nudge(1)} />
        </>
      )}
    </div>
  );
};

const RailArrow = ({ side, onClick }) => {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Previous tours" : "Next tours"}
      className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 h-11 w-11 items-center justify-center rounded-full border border-kj-line bg-kj-dark/80 backdrop-blur-sm text-kj-ink hover:border-kj-gold hover:text-kj-gold transition-colors ${
        side === "left" ? "-left-2 lg:-left-5" : "-right-2 lg:-right-5"
      }`}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
};

// A titled block of tours. layout="scroll" (swipeable rail, for the
// homepage) or layout="grid" (responsive grid, for the full listing).
// Renders nothing if empty.
export const TourSection = ({ title, subtitle, tours, layout = "grid" }) => {
  if (!tours || tours.length === 0) return null;

  return (
    <section className="w-full">
      <h3 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-kj-ink">
        {title}
      </h3>
      {subtitle && (
        <p className="mt-2 text-base text-kj-sub max-w-2xl leading-relaxed">
          {subtitle}
        </p>
      )}

      {layout === "scroll" ? (
        <TourRail tours={tours} />
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {tours.map((activity, i) => (
            <TourCard key={activity?._id || i} activity={activity} index={i} />
          ))}
        </div>
      )}
    </section>
  );
};
